import { DatePipe } from '@angular/common';
import { DateTime } from 'luxon';
import { escapeHtml } from '@app/shared/utils/helper';
import { MAX_TIMESTAMP, MIN_TIMESTAMP, UNIX_TIMESTAMP_ATTRIBUTES } from '../constants/meeting-audit-log.constants';

/**
 * Checks if a string is a valid 10-digit Unix timestamp within a reasonable range
 */
export function isTimestamp(value: string): boolean {
  if (!/^\d{10}$/.test(value)) return false;
  const timestamp = parseInt(value, 10);
  return timestamp >= MIN_TIMESTAMP && timestamp <= MAX_TIMESTAMP;
}

/**
 * Validates if a string represents an ISO 8601 datetime string
 */
export function isDateTime(value: string): boolean {
  return DateTime.fromISO(value).isValid;
}

/**
 * Check if an attribute stores a Unix timestamp (seconds since epoch)
 */
export function isUnixTimestampAttribute(attributeName: string): boolean {
  return UNIX_TIMESTAMP_ATTRIBUTES.has(attributeName);
}

/**
 * Check if an attribute stores a duration in minutes
 */
export function isDurationAttribute(attributeName: string): boolean {
  return attributeName === 'duration' || attributeName === 'early_join_time' || attributeName === 'auto_email_reminder_time';
}

/**
 * Format occurrence ID as a date. Occurrence IDs are typically Unix timestamps in milliseconds.
 * Example: "1764180000" -> "Jan 27, 2026, 12:00 AM" (if valid timestamp)
 */
export function formatOccurrenceId(occurrenceId: string, datePipe: DatePipe): string {
  if (!occurrenceId) return occurrenceId;

  const timestamp = parseInt(occurrenceId, 10);
  if (isNaN(timestamp)) return escapeHtml(occurrenceId);

  // Zoom occurrence IDs are Unix timestamps in milliseconds
  const minMs = MIN_TIMESTAMP * 1000;
  const maxMs = MAX_TIMESTAMP * 1000;

  if (timestamp >= minMs && timestamp <= maxMs) {
    return datePipe.transform(timestamp, 'MMM d, y, h:mm a') || escapeHtml(occurrenceId);
  }

  // If it looks like a 10-digit timestamp (seconds), convert to milliseconds
  if (timestamp >= MIN_TIMESTAMP && timestamp <= MAX_TIMESTAMP) {
    return datePipe.transform(timestamp * 1000, 'MMM d, y, h:mm a') || escapeHtml(occurrenceId);
  }

  return escapeHtml(occurrenceId);
}

/**
 * Sanitizes a string value for safe CSV export.
 * Prevents CSV formula injection by prefixing dangerous leading characters
 * with a single quote (OWASP recommendation).
 */
export function sanitizeForCSV(value: string): string {
  if (/^[=+\-@\t\r]/.test(value)) {
    return `'${value}`;
  }
  return value;
}
